
import type { Candidate } from "@/components/candidates/types";
import { useSourceAnalytics } from "./hooks/useSourceAnalytics";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { SourceDistributionChart } from "@/components/analytics/components/SourceDistributionChart";
import { ConversionRateChart } from "@/components/analytics/components/ConversionRateChart";

interface SourceAnalyticsProps {
  candidates: Candidate[];
}

export const SourceAnalytics = ({ candidates }: SourceAnalyticsProps) => {
  const { candidatesBySource, conversionRateBySource } = useSourceAnalytics(candidates);

  if (!candidates || candidates.length === 0) {
    return (
      <Card className="overflow-hidden">
        <CardHeader>
          <CardTitle>Source Analytics</CardTitle>
          <CardDescription>No candidate data available for analysis</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center h-[300px] text-muted-foreground bg-muted/5 rounded-lg border border-dashed">
            Add candidates to see source analytics
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="overflow-hidden">
      <CardHeader>
        <CardTitle>Source Analytics</CardTitle>
        <CardDescription>Distribution and effectiveness of candidate sources</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Source Distribution */}
          <div className="flex flex-col">
            <h3 className="text-lg font-medium mb-2">Source Distribution</h3>
            <p className="text-sm text-muted-foreground mb-4">Total candidates by recruitment source</p>
            <div className="grow h-[300px] w-full">
              {candidatesBySource.length > 0 ? (
                <SourceDistributionChart data={candidatesBySource} candidates={candidates} />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground bg-muted/5 rounded-lg border border-dashed">
                  No source data available
                </div>
              )}
            </div>
          </div>
          
          {/* Conversion Rate */}
          <div className="flex flex-col">
            <h3 className="text-lg font-medium mb-2">Source to Hire Conversion</h3>
            <p className="text-sm text-muted-foreground mb-4">Percentage of candidates hired by source</p>
            <div className="grow h-[300px] w-full">
              {conversionRateBySource.length > 0 ? (
                <ConversionRateChart data={conversionRateBySource} candidates={candidates} />
              ) : (
                <div className="flex items-center justify-center h-full text-muted-foreground bg-muted/5 rounded-lg border border-dashed">
                  No conversion data available
                </div>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};
